import { useState, useRef } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Button, Menu, MenuItem, ButtonGroup } from "@mui/material";

import { CONFIGURATION_LISTS } from "appConfig";

import { Loading } from "classes/Loading";

import { ConcurrencyModal } from "components/ConcurrencyModal";
import { Tooltip } from "components/Tooltip";

import { i18n } from "services/i18nService";
import { isValid as isValidAdjustment } from "services/sosInventoryService/adjustment/isValid";
import { isValid as isValidAlert } from "services/sosInventoryService/alert/isValid";
import { isValid as isValidAopRule } from "services/sosInventoryService/aopRule/isValid";
import { isValid as isValidBin } from "services/sosInventoryService/bin/isValid";
import { isValid as isValidBuild } from "services/sosInventoryService/build/isValid";
import { isValid as isValidCart } from "services/sosInventoryService/cart/isValid";
import { isValid as isValidCustomField } from "services/sosInventoryService/customField/isValid";
import { isValid as isValidCustomer } from "services/sosInventoryService/customer/isValid";
import { isValid as isValidDocument } from "services/sosInventoryService/document/isValid";
import { isValid as isValidEstimate } from "services/sosInventoryService/estimate/isValid";
import { isValid as isValidFormTemplate } from "services/sosInventoryService/formTemplate/isValid";
import { isValid as isValidFtpConnection } from "services/sosInventoryService/ftpConnection/isValid";
import { isValid as isValidInvoice } from "services/sosInventoryService/invoice/isValid";
import { isValid as isValidItem } from "services/sosInventoryService/item/isValid";
import { isValid as isValidItemReceipt } from "services/sosInventoryService/itemReceipt/isValid";
import { isValid as isValidJob } from "services/sosInventoryService/job/isValid";
import { isValid as isValidLot } from "services/sosInventoryService/lot/isValid";
import { nameIsValid } from "services/sosInventoryService/nameIsValid";
import { isValid as isValidPayment } from "services/sosInventoryService/payment/isValid";
import { isValid as isValidPickTicket } from "services/sosInventoryService/pickTicket/isValid";
import { isValid as isValidPriceTier } from "services/sosInventoryService/priceTier/isValid";
import { isValid as isValidPriority } from "services/sosInventoryService/priority/isValid";
import { isValid as isValidProcess } from "services/sosInventoryService/process/isValid";
import { isValid as isValidProcessTemplate } from "services/sosInventoryService/processTemplate/isValid";
import { isValid as isValidPurchaseOrder } from "services/sosInventoryService/purchaseOrder/isValid";
import { isValid as isValidRental } from "services/sosInventoryService/rental/isValid";
import { isValid as isValidRentalReturn } from "services/sosInventoryService/rentalReturn/isValid";
import { isValid as isValidReorder } from "services/sosInventoryService/reorder/isValid";
import { isValid as isValidReturn } from "services/sosInventoryService/return/isValid";
import { isValid as isValidReturnToVendor } from "services/sosInventoryService/returnToVendor/isValid";
import { isValid as isValidRma } from "services/sosInventoryService/rma/isValid";
import { isValid as isValidSalesOrder } from "services/sosInventoryService/salesOrder/isValid";
import { isValid as isValidSalesReceipt } from "services/sosInventoryService/salesReceipt/isValid";
import { isValid as isValidSalesRep } from "services/sosInventoryService/salesRep/isValid";
import { isValid as isValidSerial } from "services/sosInventoryService/serial/isValid";
import { isValid as isValidShipment } from "services/sosInventoryService/shipment/isValid";
import {
  putRecord,
  postRecord,
  deleteRecords,
} from "services/sosInventoryService/sosApi";
import { isValid as isValidSyncItem } from "services/sosInventoryService/syncItem/isValid";
import { isValid as isValidTask } from "services/sosInventoryService/task/isValid";
import { isValid as isValidLibraryTemplate } from "services/sosInventoryService/templateLibrary/isValid";
import { isValid as isValidTransfer } from "services/sosInventoryService/transfer/isValid";
import { isValid as isValidUom } from "services/sosInventoryService/uom/isValid";
import { isValid as isValidUpsShipment } from "services/sosInventoryService/upsShipment/isValid";
import { isValid as isValidUser } from "services/sosInventoryService/user/isValid";
import { isValid as isValidVendor } from "services/sosInventoryService/vendor/isValid";
import { isValid as isValidVendorItem } from "services/sosInventoryService/vendorItem/isValid";
import { isValid as isValidWarranty } from "services/sosInventoryService/warranty/isValid";
import { isValid as isValidWorkCenter } from "services/sosInventoryService/workCenter/isValid";
import { isValid as isValidWorkOrder } from "services/sosInventoryService/workOrder/isValid";
import { isValid as isValidWorker } from "services/sosInventoryService/worker/isValid";
import { setPageDirty } from "services/utility/edit";
import { handleProgramError } from "services/utility/errors";

import { useListNavigation } from "hooks/useListNavigation";
import { usePrivileges } from "hooks/usePrivileges";

import { initialErrorState } from "views/common";

import { openAlert } from "globalState/alertSlice";
import { openDialog } from "globalState/globalDialogSlice";
import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";
import { closeModal, openModal } from "globalState/modalSlice";
import { deleteNotifications } from "globalState/notificationsSlice";
import { recordsChangedTick } from "globalState/recordsChangedSlice";

import {
  OBJECT_TYPES,
  VIEW_COST_REQUIRED,
  USER_PRIVILEGE,
  CONCURRENCY_ENABLED_OBJECT_TYPES,
} from "appConstants";
import {
  getErrorCodeMessage,
  CONCURRENCY_CONFLICT,
  INVALID_SHOPIFY_ACCESS_TOKEN,
} from "appErrorCodes";
import { saveActions } from "editConfig";

export const isValid = {
  adjustment: isValidAdjustment,
  alert: isValidAlert,
  aoprule: isValidAopRule,
  bins: isValidBin,
  build: isValidBuild,
  cart: isValidCart,
  class: nameIsValid,
  channel: nameIsValid,
  customer: isValidCustomer,
  customfield: isValidCustomField,
  customermessage: nameIsValid,
  customertype: nameIsValid,
  department: nameIsValid,
  document: isValidDocument,
  estimate: isValidEstimate,
  formtemplate: isValidFormTemplate,
  ftpconnection: isValidFtpConnection,
  invoice: isValidInvoice,
  item: isValidItem,
  itemreceipt: isValidItemReceipt,
  job: isValidJob,
  location: nameIsValid,
  lot: isValidLot,
  orderstage: nameIsValid,
  payment: isValidPayment,
  paymentmethod: nameIsValid,
  pickticket: isValidPickTicket,
  pricetier: isValidPriceTier,
  purchaseorder: isValidPurchaseOrder,
  process: isValidProcess,
  processtemplate: isValidProcessTemplate,
  priority: isValidPriority,
  rental: isValidRental,
  rentalreturn: isValidRentalReturn,
  reorder: isValidReorder,
  return: isValidReturn,
  returntovendor: isValidReturnToVendor,
  rma: isValidRma,
  salesorder: isValidSalesOrder,
  salesreceipt: isValidSalesReceipt,
  salesrep: isValidSalesRep,
  serial: isValidSerial,
  shipment: isValidShipment,
  shipmethod: nameIsValid,
  syncitem: isValidSyncItem,
  tag: nameIsValid,
  task: isValidTask,
  terms: nameIsValid,
  templatelibrary: isValidLibraryTemplate,
  transfer: isValidTransfer,
  uom: isValidUom,
  user: isValidUser,
  upsshipment: isValidUpsShipment,
  vendor: isValidVendor,
  vendoritem: isValidVendorItem,
  vendormessage: nameIsValid,
  warranty: isValidWarranty,
  workorder: isValidWorkOrder,
  workcenter: isValidWorkCenter,
  worker: isValidWorker,
};

export function SaveButtonSuite(props) {
  const {
    objectType,
    setReloadRecord,
    record,
    relatedRecords,
    lines,
    inputs,
    outputs,
    stages,
    totals,
    otherCosts,
    setErrors,
    errors,
    vendorId,
    customerId,
    onAdd,
    fromForm,
    newFromId,
    disableSave,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector((state) => state.loading.editModal);

  const { goToList } = useListNavigation(objectType);

  const [anchorEl, setAnchorEl] = useState(null);
  const [concurrencyRecord, setConcurrencyRecord] = useState(null);
  const [afterSaveOption, setAfterSaveOption] = useState(null);

  const anchorRef = useRef();
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const isSavingRef = useRef(false);
  const { hasPrivilegesOrIsAdmin } = usePrivileges();
  const viewCosts = hasPrivilegesOrIsAdmin(USER_PRIVILEGE.viewCosts);

  const isUpsShipment = objectType === OBJECT_TYPES.UPS_SHIPMENT.fullString;

  // Check if this is an existing record (has an id) vs a new record being created
  const isPersistedRecord = Boolean(record?.id);

  function isLineCalculating(line) {
    return line.cost instanceof Loading || line.valueDiff instanceof Loading;
  }

  const costLoading = lines?.some(isLineCalculating);

  function getObjectIdentifier(record) {
    if (record.number) {
      return record.number;
    }
    if (record.name) {
      return record.name;
    }
    return "";
  }

  // afterSave can be "close", "keepEditing", "new", "goTo", "pdf", or "email"
  async function save(afterSave, concurrencyOverride = false) {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    dispatch(editModalLoadingIndicatorOn());
    try {
      // Flush pending React state (e.g. blur-triggered valueDiff update) and
      // force blur if an input is still focused (keyboard/programmatic save paths)
      const activeInput = document.activeElement;
      flushSync(() => {
        if (activeInput?.tagName === "INPUT") activeInput.blur();
      });

      // Cost basis is async; wait up to 5s for any Loading instances to resolve
      const deadline = Date.now() + 5000;
      while (
        Date.now() < deadline &&
        linesRef.current?.some(isLineCalculating)
      ) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      if (linesRef.current?.some(isLineCalculating)) {
        setErrors((prev) => ({
          ...prev,
          messages: [i18n("global.UnableToSave")],
        }));
        return;
      }

      if (errors?.customFieldErrors?.length) {
        // check for customFieldErrors seperately as those are
        // dependant on a user updating the field to an incorrect
        // value and not just the state of the value itself. This
        // customFieldErrors value can set in CustomFieldDatePicker.js
        setErrors((prev) => ({
          ...prev,
          messages: prev.customFieldErrors.map(({ message }) => message),
        }));
        return;
      }
      // if the record is not in a valid state...
      const isValidErrors = isValid[objectType](
        record,
        relatedRecords,
        linesRef.current,
        outputs,
        inputs
      );
      if (isValidErrors.messages.length) {
        setErrors(isValidErrors);
        return;
      }
      const completeRecord = {
        ...record,
        lines: linesRef.current,
        otherCosts,
        totals,
        workcenters: stages,
        inputs: inputs,
        outputs: outputs,
        ...(isPersistedRecord && { forceSave: concurrencyOverride }),
      };

      // Use PUT for updates, POST for new records
      const persistenceFunc = isPersistedRecord ? putRecord : postRecord;
      const {
        success,
        record: newRecord,
        message,
        errorCode,
        details,
      } = await persistenceFunc(objectType, completeRecord, newFromId);
      if (success) {
        const message = `${i18n(
          `objectType.${objectType}.Sentence`
        )} ${getObjectIdentifier(newRecord)} ${i18n("alert.SuccessSave")}`;
        dispatch(openAlert({ type: "success", message }));
        if (onAdd) {
          onAdd(newRecord);
        } else {
          setPageDirty(false);
          const linkPrefix = CONFIGURATION_LISTS.includes(objectType)
            ? "/configuration/list"
            : "";
          switch (afterSave) {
            case "close":
              dispatch(closeModal());
              dispatch(recordsChangedTick());
              goToList();
              break;
            case "new":
              dispatch(closeModal());
              // Wait for modal to close before opening a new one to ensure clean state
              setTimeout(() => {
                dispatch(openModal({ objectType }));
              }, 0);
              break;
            case "keepEditing":
              setReloadRecord(newRecord);
              break;
            case "goTo":
              dispatch(closeModal());
              dispatch(recordsChangedTick());
              navigate(`${linkPrefix}/${objectType}?id=${newRecord.id}`);
              break;
            case "ship":
              dispatch(closeModal());
              dispatch(
                openModal({
                  objectType: OBJECT_TYPES.SHIPMENT.fullString,
                  modalProps: {
                    newFromObjectType: objectType,
                    newFromId: newRecord.id,
                  },
                })
              );
              break;
            case "pdf":
              dispatch(closeModal());
              goToList();
              dispatch(recordsChangedTick());
              dispatch(
                openDialog({
                  type: "pdf",
                  dialogProps: {
                    objectType: objectType,
                    objectIds: [newRecord.id],
                    recordNumber: newRecord.number,
                  },
                })
              );
              break;
            case "printLabel":
              dispatch(closeModal());
              goToList();
              dispatch(recordsChangedTick());
              dispatch(
                openDialog({
                  type: "printLabel",
                  dialogProps: {
                    objectType: objectType,
                    ids: [newRecord.id],
                    recordNumber: newRecord.number,
                  },
                })
              );
              break;
            case "email":
              dispatch(closeModal());
              goToList();
              dispatch(recordsChangedTick());
              dispatch(
                openDialog({
                  type: "email",
                  dialogProps: {
                    objectIds: [newRecord.id],
                    objectType: objectType,
                    documents: newRecord.documents,
                    recordNumber: newRecord.number,
                    billing: newRecord.billing,
                    shipping: newRecord.shipping,
                    vendorId,
                    customerId,
                  },
                })
              );
              break;
            default:
              handleProgramError(
                new Error(`unexpected value for afterSave (${afterSave})`)
              );
          }
          setErrors(initialErrorState());
        }
        if (
          objectType === OBJECT_TYPES.TASK.fullString &&
          record.deleteNotification &&
          record.notificationId
        ) {
          const { success } = await deleteRecords("notification", [
            record.notificationId,
          ]);
          if (success) {
            dispatch(deleteNotifications([record.notificationId]));
          } else {
            handleProgramError(
              new Error("Unable to delete associated notification")
            );
          }
        }
      } else if (
        errorCode === CONCURRENCY_CONFLICT &&
        CONCURRENCY_ENABLED_OBJECT_TYPES.includes(objectType)
      ) {
        setConcurrencyRecord(details.data);
        setAfterSaveOption(afterSave);
      } else if (errorCode === INVALID_SHOPIFY_ACCESS_TOKEN) {
        setErrors((prev) => ({ ...prev, messages: [message] }));
        setReloadRecord((prev) => ({
          ...prev,
          authorizationField1: null,
          enabled: false,
        }));
      } else if (errorCode) {
        const errorMessage = getErrorCodeMessage(errorCode, details);
        setErrors((prev) => ({ ...prev, messages: [errorMessage || message] }));
      } else {
        setErrors((prev) => ({ ...prev, messages: [message] }));
      }
    } finally {
      dispatch(editModalLoadingIndicatorOff());
      isSavingRef.current = false;
    }
  }

  function handleOperation(operation) {
    setAnchorEl(null);
    operation();
  }

  function handleOverwrite() {
    dispatch(editModalLoadingIndicatorOn());
    setConcurrencyRecord(null);
    save(afterSaveOption, true);
  }

  function handleSetToConcurrencyRecord() {
    setReloadRecord(concurrencyRecord);
    setConcurrencyRecord(null);
  }

  function getShowAction(type) {
    if (!saveActions[objectType].includes(type)) {
      return false;
    }
    if (!VIEW_COST_REQUIRED[type]?.includes(objectType)) {
      return true;
    }
    return viewCosts;
  }

  function showMore() {
    return Boolean(saveActions[objectType].length);
  }

  return (
    <>
      {!fromForm && showMore() && (
        <div style={{ width: "6em" }}>
          <ButtonGroup fullWidth>
            <Button
              ref={anchorRef}
              variant="outlined"
              onClick={() => setAnchorEl(anchorRef.current)}
              sx={{ backgroundColor: "white" }}
              disabled={disableSave || isLoading}
            >
              {i18n("saveActions.More")}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => setAnchorEl(anchorRef.current)}
              disableElevation
              disabled={disableSave || isLoading}
            >
              <KeyboardArrowDownIcon />
            </Button>
          </ButtonGroup>
        </div>
      )}
      <div>
        <Tooltip title={costLoading ? i18n("tooltip.SaveDisabled") : ""}>
          <span>
            <Button
              variant="contained"
              onClick={() => save("close")}
              data-testing="saveAndClose"
              disableElevation
              fullWidth
              disabled={disableSave || isLoading}
            >
              {isUpsShipment
                ? i18n("saveActions.ShipNow")
                : i18n("saveActions.SaveAndClose")}
            </Button>
          </span>
        </Tooltip>
      </div>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        keepMounted
        onClose={() => setAnchorEl(null)}
        disableScrollLock={true}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {getShowAction("goTo") && (
          <MenuItem onClick={() => handleOperation(async () => save("goTo"))}>
            {i18n("saveActions.SaveAndGoTo")}
          </MenuItem>
        )}
        {getShowAction("keepEditing") && (
          <MenuItem onClick={() => handleOperation(() => save("keepEditing"))}>
            {i18n("saveActions.SaveAndKeepEditing")}
          </MenuItem>
        )}
        {getShowAction("new") && (
          <MenuItem onClick={() => handleOperation(() => save("new"))}>
            {i18n("saveActions.SaveAndNew")}
          </MenuItem>
        )}
        {getShowAction("pdf") && (
          <MenuItem onClick={() => handleOperation(() => save("pdf"))}>
            {i18n("saveActions.SaveAndPDF")}
          </MenuItem>
        )}
        {getShowAction("printLabel") && (
          <MenuItem onClick={() => handleOperation(() => save("printLabel"))}>
            {i18n("saveActions.SaveAndPrintLabel")}
          </MenuItem>
        )}
        {getShowAction("email") && (
          <MenuItem onClick={() => handleOperation(() => save("email"))}>
            {i18n("saveActions.SaveAndSend")}
          </MenuItem>
        )}
        {getShowAction("ship") && (
          <MenuItem onClick={() => handleOperation(() => save("ship"))}>
            {i18n("saveActions.SaveAndShip")}
          </MenuItem>
        )}
      </Menu>
      {Boolean(concurrencyRecord) && (
        <ConcurrencyModal
          onOverwrite={handleOverwrite}
          onRefresh={handleSetToConcurrencyRecord}
          onClose={() => setConcurrencyRecord(null)}
          objectType={objectType}
        />
      )}
    </>
  );
}
